import type { CategorySlug } from '@/config/categories';

export interface CategoryColorClasses {
  textColor: string;
  bgColor: string;
  borderColor: string;
  hoverColor: string;
}

/**
 * Tailwind class quads keyed to the closed ten-category taxonomy (WEB-692).
 * Typed against `CategorySlug` so a new canonical category fails compile
 * until a color entry lands here.
 */
export const CATEGORY_COLORS = {
  music: {
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-300',
    hoverColor: 'hover:bg-purple-200',
  },
  sports: {
    textColor: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
    hoverColor: 'hover:bg-green-200',
  },
  food: {
    textColor: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
    hoverColor: 'hover:bg-red-200',
  },
  'science-tech': {
    textColor: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
    hoverColor: 'hover:bg-blue-200',
  },
  art: {
    textColor: 'text-violet-700',
    bgColor: 'bg-violet-100',
    borderColor: 'border-violet-300',
    hoverColor: 'hover:bg-violet-200',
  },
  drinks: {
    textColor: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
    hoverColor: 'hover:bg-orange-200',
  },
  'business-networking': {
    textColor: 'text-slate-700',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    hoverColor: 'hover:bg-slate-200',
  },
  'community-culture': {
    textColor: 'text-fuchsia-700',
    bgColor: 'bg-fuchsia-100',
    borderColor: 'border-fuchsia-300',
    hoverColor: 'hover:bg-fuchsia-200',
  },
  wellness: {
    textColor: 'text-lime-700',
    bgColor: 'bg-lime-100',
    borderColor: 'border-lime-300',
    hoverColor: 'hover:bg-lime-200',
  },
  // Deliberate share with community-culture (O-1 ruling) — not an oversight.
  'hobbies-interests': {
    textColor: 'text-fuchsia-700',
    bgColor: 'bg-fuchsia-100',
    borderColor: 'border-fuchsia-300',
    hoverColor: 'hover:bg-fuchsia-200',
  },
} as const satisfies Record<CategorySlug, CategoryColorClasses>;

const DEFAULT_COLORS: CategoryColorClasses = {
  textColor: 'text-gray-700',
  bgColor: 'bg-gray-100',
  borderColor: 'border-gray-300',
  hoverColor: 'hover:bg-gray-200',
};

/**
 * HEX color map for SVG markers on the map. Keyed by lowercase category slug.
 * Values match the `:root --cat-*` tokens in `styles/tokens/color.css` (WEB-692 / O-1).
 * SVG fill attributes require raw hex; Tailwind class quads live in CATEGORY_COLORS.
 *
 * `hobbies-interests` deliberately shares `community-culture`'s hue.
 */
export const CATEGORY_HEX_COLORS = {
  music: '#7c3aed',
  sports: '#16a34a',
  food: '#dc2626',
  'science-tech': '#2563eb',
  art: '#ec4899',
  drinks: '#ea580c',
  'business-networking': '#6579a7',
  'community-culture': '#b64ebd',
  wellness: '#85971c',
  'hobbies-interests': '#b64ebd',
} as const satisfies Record<CategorySlug, string>;

/**
 * Get Tailwind color classes for a category based on its slug.
 * NOTE: hexColor is intentionally unused — colors are resolved only from the
 * slug-keyed CATEGORY_COLORS map. Unknown slugs fall back to gray (a taxonomy
 * error, not a color — WEB-692 retired the orange DEFAULT_CATEGORY_COLOR).
 */
export function getColorClasses(
  _hexColor: string | null | undefined,
  categorySlug: string,
): CategoryColorClasses {
  if (Object.hasOwn(CATEGORY_COLORS, categorySlug)) {
    return CATEGORY_COLORS[categorySlug as CategorySlug];
  }
  return DEFAULT_COLORS;
}

/**
 * Resolve a category slug to its marker hex, or `undefined` when the slug is
 * not an own key of {@link CATEGORY_HEX_COLORS} (WEB-692 — inherited keys like
 * `toString` / `constructor` must not win over the skyline fallback).
 */
export function getCategoryHexColor(categorySlug: string): string | undefined {
  if (Object.hasOwn(CATEGORY_HEX_COLORS, categorySlug)) {
    return CATEGORY_HEX_COLORS[categorySlug as CategorySlug];
  }
  return undefined;
}

/** Theme-aware derived chroma set for a category (WEB-701). */
export interface CategoryChroma {
  /** Base hue — from {@link CATEGORY_HEX_COLORS}. */
  cat: string;
  /** Fill: base hue + alpha suffix (`2E` dark / `1A` light). */
  catSoft: string;
  /** Border: base hue + alpha suffix (`8C` dark / `66` light). */
  catLine: string;
  /** Text: {@link deriveCategoryInk} of the base hue for this theme. */
  catInk: string;
}

/** Two-digit hex alpha suffixes appended to the base hue for `catSoft` / `catLine`. */
const CAT_SOFT_ALPHA: Record<'light' | 'dark', string> = { dark: '2E', light: '1A' };
const CAT_LINE_ALPHA: Record<'light' | 'dark', string> = { dark: '8C', light: '66' };

/**
 * Share of the base hue retained by the O-1 signature mix — the `45%` in
 * `color-mix(in srgb, var(--cat) 45%, <target>)`.
 */
const CAT_INK_MIX_RATIO = 0.45;

/**
 * The other half of the O-1 mix: `#FFF` in dark, `--loci-indigo` in light
 * (`#1E2B4B`, resolved from `styles/tokens/aliases.css` `--brand-accent`). The base
 * hue is theme-invariant, so this target is the *only* thing carrying the
 * light/dark difference — there is no `[data-theme="light"]` ink table.
 */
const CAT_INK_MIX_TARGET: Record<'light' | 'dark', string> = {
  dark: '#FFFFFF',
  light: '#1E2B4B',
};

/** Split a 6-digit `#rrggbb` string into its three 0–255 channels. */
function hexChannels(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

/**
 * Derive a category's ink from its base hue — the single ink system for the
 * whole surface (WEB-728). Implements the O-1 signature formula
 * `color-mix(in srgb, var(--cat) 45%, #FFF | var(--loci-indigo))` in TS so
 * callers that need a concrete hex (SVG fills, canvas, inline styles, tests
 * that measure contrast) get the same value CSS would resolve.
 *
 * WEB-701 originally shipped a *second*, hand-declared per-slug ink table
 * alongside this formula. It is retired: one of its light values measured
 * 4.43:1 against `--bg-sunken` (`#eef1f6`), missing the 4.5:1 floor, and three
 * more sat within 0.25 of it. Every value this formula produces clears 4.5:1
 * against **both** `--bg` and `--bg-sunken` in both themes, worst case
 * `wellness` light at 6.41:1. Ruling, evidence, and the full 40-measurement
 * table: `docs/design-system/event-v2-category-ink-ruling.md`.
 *
 * Matches CSS `color-mix(in srgb, …)` for opaque colors: a straight
 * per-channel interpolation of non-linearized sRGB values. The result is
 * rounded to the nearest 8-bit channel, so it can differ from a browser's
 * internal float by at most half a step — far below any contrast threshold.
 */
export function deriveCategoryInk(base: string, theme: 'light' | 'dark'): string {
  const baseChannels = hexChannels(base);
  const targetChannels = hexChannels(CAT_INK_MIX_TARGET[theme]);
  const mixed = baseChannels.map((channel, index) =>
    Math.round(channel * CAT_INK_MIX_RATIO + targetChannels[index] * (1 - CAT_INK_MIX_RATIO)),
  );
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/**
 * Compose a {@link CategoryChroma} from a base hue — the one place that
 * appends the alpha suffixes and derives the ink, shared by the fallback table
 * below and {@link getCategoryChroma} so the two paths can't drift apart.
 */
function buildChroma(base: string, theme: 'light' | 'dark'): CategoryChroma {
  return {
    cat: base,
    catSoft: `${base}${CAT_SOFT_ALPHA[theme]}`,
    catLine: `${base}${CAT_LINE_ALPHA[theme]}`,
    catInk: deriveCategoryInk(base, theme),
  };
}

/**
 * Fallback chroma for unknown category slugs — the CLT skyline. Mirrors
 * `GoogleMap.tsx`'s `resolveMarkerColor` fallback (`#0E7B7B`, i.e.
 * `--clt-skyline`) for `cat`, so an unmapped category still resolves to a
 * legible, on-brand chroma rather than an arbitrary gray. There is no existing
 * `--clt-skyline` dark hex constant in this file, so the dark base (`#2FA3A3`)
 * is inlined from `styles/tokens/dark.css`'s `[data-theme='dark'] --clt-skyline`.
 *
 * Its ink runs through {@link deriveCategoryInk} like every category's, rather
 * than declaring `--clt-skyline-glow-fg` (`#0A6363` / `#4FC4C4`) as a separate
 * value — WEB-728 leaves exactly one ink derivation in this module, with no
 * hand-declared exception for the fallback path. The derived inks clear AA on
 * every ground by a wider margin than the glow tokens did: light `#174F61` is
 * 9.02:1 on `--bg` / 7.97:1 on `--bg-sunken`, dark `#A1D6D6` is 11.49:1 /
 * 12.35:1 (asserted in `__tests__/categoryColors.test.ts`).
 */
const CATEGORY_CHROMA_FALLBACK: Record<'light' | 'dark', CategoryChroma> = {
  light: buildChroma('#0E7B7B', 'light'),
  dark: buildChroma('#2FA3A3', 'dark'),
};

/**
 * Derive the four-value chroma set (fill/border/ink) a category tints itself
 * with, for the given theme — the eyebrow chip, section ordinals, row leads,
 * thumb gradients, map pins, and run-of-dates calendar all read this set
 * rather than a single hue (WEB-701).
 *
 * Pure — every value derives from {@link CATEGORY_HEX_COLORS} alone (alpha
 * suffix for the fills, {@link deriveCategoryInk} for the ink), so there is no
 * second hue or ink table to keep in sync. Unknown slugs, including inherited
 * `Object` keys like `toString` (WEB-692's `Object.hasOwn` guard), return
 * {@link CATEGORY_CHROMA_FALLBACK}.
 */
export function getCategoryChroma(slug: string, theme: 'light' | 'dark'): CategoryChroma {
  if (!Object.hasOwn(CATEGORY_HEX_COLORS, slug)) {
    // Fresh copy — CATEGORY_CHROMA_FALLBACK[theme] is a shared module-level
    // object; handing out the reference directly would let a caller mutate
    // it and corrupt every subsequent unknown-slug lookup, breaking this
    // function's documented pure-resolver contract.
    return { ...CATEGORY_CHROMA_FALLBACK[theme] };
  }
  return buildChroma(CATEGORY_HEX_COLORS[slug as CategorySlug], theme);
}
